/* eslint-disable react/prop-types */
'use client';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Contact, Contact2, Loader2, Mail, User, User as UserIcon } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { vendorApi, prepareVendorUpdateData } from '@/pages/vendor/api/api';

const VendorProfileEdit = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [alternateContact, setAlternateContact] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, refreshUser } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.id) {
      setName(user.name || '');
      setEmail(user.emailId || '');
      setAlternateContact(user.alternateContact || '');
    }
  }, [user]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    const updates = {
      name,
      emailId: email,
      alternateContact,
      storeFront: user?.storeFront,
      viewOnly: user?.viewOnly,
    };

    const apiData = prepareVendorUpdateData(user, updates);
    console.log(apiData);

    try {
      const result = await vendorApi.updateVendor(apiData);
      if (result.success) {
        await refreshUser(user.contactNo);
        toast.success('Profile updated successfully!');
        navigate('/profile');
      } else {
        toast.error(result.message || 'Failed to update profile.');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('An error occurred while updating profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col pb-16 min-h-screen bg-slate-50">
      {/* Main content with responsive two-column grid */}
      <main className=" mx-auto px-4 pt-6 pb-24 md:p-4 sm:p-6 lg:p-8">
        <div className="md:grid md:grid-cols-5 md:gap-10">
          {/* --- LEFT COLUMN: VISUAL IDENTITY (Desktop Only) --- */}
          <div className="hidden md:flex md:col-span-2 flex-col items-center text-center bg-white/50 backdrop-blur-lg p-8 rounded-2xl border border-slate-200/60 h-fit sticky top-8">
            <div className="relative">
              <div className="bg-white rounded-full p-2">
                <User className="size-24" />
              </div>
              <div className="absolute -inset-1.5 rounded-full border-2 border-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-border animate-[spin_8s_linear_infinite] -z-10" />
            </div>
            <h2 className="mt-6 text-2xl font-bold text-slate-800">{user?.name}</h2>
            <p className="mt-2 text-slate-500">
              Your profile is a reflection of your business. Keeping it up-to-date helps build trust
              with your customers.
            </p>
            <div className="mt-6 h-1 w-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" />
          </div>

          {/* --- RIGHT COLUMN: EDITING FORM --- */}
          <div className="md:col-span-3">
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-slate-200/80">
              {/* Mobile Back Button */}
              <button
                onClick={() => navigate(-1)}
                className="md:hidden flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 -ml-2 p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <ArrowLeft className="size-5" />
                <span className="font-medium">Back</span>
              </button>

              <div className="mb-8">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Edit Profile</h1>
                <p className="text-slate-500 mt-1">
                  Make changes to your personal information below.
                </p>
              </div>
              <form className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-semibold text-slate-800">
                    Full Name
                  </Label>
                  <div className="flex focus:border-slate-900 items-center group rounded-md bg-slate-100 gap-3 pl-4 border border-slate-300 w-full">
                    <UserIcon className="size-6" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="py-2 bg-slate-100 focus:outline-none px-4 w-full"
                      name="name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-semibold text-slate-800">
                    Email Id
                  </Label>
                  <div className="flex focus:border-slate-900 items-center group rounded-md bg-slate-100 gap-3 pl-4 border border-slate-300 w-full">
                    <Mail className="size-6" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="py-2 bg-slate-100 focus:outline-none px-4 w-full"
                      name="email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactNo" className="font-semibold text-slate-800">
                    Primary Contact No
                  </Label>
                  <div className="flex focus:border-slate-900 items-center group rounded-md bg-slate-100 gap-3 pl-4 border border-slate-300 w-full">
                    <Contact2 className="size-6" />
                    <input
                      type="text"
                      name="contactNo"
                      value={user?.contactNo}
                      readOnly
                      className="py-2 bg-slate-100 focus:outline-none px-4 w-full"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="alternateContact" className="font-semibold text-slate-800">
                    Alternate Contact (Optional)
                  </Label>
                  <div className="flex focus:border-slate-900 items-center group rounded-md bg-slate-100 gap-3 pl-4 border border-slate-300 w-full">
                    <Contact className="size-6" />
                    <input
                      type="tel"
                      value={alternateContact}
                      onChange={(e) => setAlternateContact(e.target.value)}
                      className="py-2 bg-slate-100 focus:outline-none px-4 w-full"
                      name="alternateContact"
                    />
                  </div>
                </div>

                <div className="pt-4 flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="text-slate-600 hover:bg-slate-100"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    onClick={handleUpdate}
                    className="bg-blue-600 hover:bg-blue-700 font-semibold px-6"
                    disabled={loading}
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VendorProfileEdit;
